import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CANDIDATE_STAGE_LABELS } from "../../utils/stages.js";
import { isClosedCandidateStage } from "@/shared/candidate-stages.js";
import { parseUTC } from "../../utils/date.js";
import { useAuthStore } from "../../store/auth.js";
import { api } from "../../utils/api.js";

interface JobDetailData {
  slug: string;
  title: string;
  team: string;
  location: string;
  status: string;
  descriptionHtml: string;
}

interface ApplicationStatus {
  applied: boolean;
  stage?: string;
  appliedAt?: string;
}

function formatDate(dateStr: string): string {
  return parseUTC(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function JobDetail() {
  const { slug } = useParams<{ slug: string }>();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isEmployee = user?.role === "employee";
  const [job, setJob] = useState<JobDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [appStatus, setAppStatus] = useState<ApplicationStatus | null>(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    if (!slug) return;

    fetch(`/api/v1/jobs/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then((data: JobDetailData) => setJob(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    fetch(`/api/v1/applications/status/${slug}`)
      .then((r) => r.json())
      .then((data: ApplicationStatus) => setAppStatus(data))
      .catch(() => setAppStatus({ applied: false }));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-gray-400">Loading...</div>
    );
  }

  if (notFound || !job) {
    return (
      <div className="py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Position not found</h1>
        <p className="mt-2 text-gray-500">This role may have been closed or removed.</p>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{job.title}</h1>
        <div className="mt-2 flex gap-3 text-sm text-gray-500">
          <span>{job.team}</span>
          <span>&middot;</span>
          <span>{job.location}</span>
        </div>
      </div>

      <div
        className="prose-custom mt-8"
        dangerouslySetInnerHTML={{ __html: job.descriptionHtml }}
      />

      {!isEmployee && (
        <div className="mt-10 border-t border-gray-200 pt-8">
          {appStatus?.applied ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-medium text-green-800">
                You have already applied for this position
                {appStatus.appliedAt && <> on {formatDate(appStatus.appliedAt)}</>}.
              </p>
              {appStatus.stage && (
                <p className="mt-1 text-sm text-green-700">
                  Status:{" "}
                  <span className="font-medium">
                    {CANDIDATE_STAGE_LABELS[appStatus.stage] ?? appStatus.stage}
                  </span>
                </p>
              )}
              <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
                <button onClick={logout} className="text-gray-500 underline hover:text-gray-700">
                  Sign out
                </button>
                {!isClosedCandidateStage(appStatus.stage ?? "") && (
                  <>
                    <span>&middot;</span>
                    {showWithdrawConfirm ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-red-600">Withdraw your application?</span>
                        <button
                          onClick={async () => {
                            setWithdrawing(true);
                            try {
                              await api.post(`/applications/withdraw/${slug}`);
                              setAppStatus((prev) =>
                                prev ? { ...prev, stage: "withdrawn" } : prev,
                              );
                            } finally {
                              setWithdrawing(false);
                              setShowWithdrawConfirm(false);
                            }
                          }}
                          disabled={withdrawing}
                          className="text-red-600 underline hover:text-red-800 font-medium"
                        >
                          {withdrawing ? "Withdrawing..." : "Yes, withdraw"}
                        </button>
                        <button
                          onClick={() => setShowWithdrawConfirm(false)}
                          className="text-gray-500 underline hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setShowWithdrawConfirm(true)}
                        className="text-gray-500 underline hover:text-gray-700"
                      >
                        Withdraw application
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <Link
              to={`/jobs/${job.slug}/apply`}
              className="inline-flex items-center rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-[var(--action-primary-hover)]"
            >
              Apply for this position
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
